import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import { Button } from "@/components/ui/button";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignMilpitas = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Craftsman ADU Design & Construction in Milpitas",
    "description": "Professional Craftsman ADU design and construction services in Milpitas, CA. Custom accessory dwelling units with authentic craftsman architectural style.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://udaadu.com"
    },
    "areaServed": "Milpitas, CA",
    "serviceType": "Craftsman ADU Design & Construction"
  };

  return (
    <>
      <CanonicalTag url="https://udaadu.com/craftsman-adu-design-milpitas" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-amber-900 via-orange-900 to-red-900">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Craftsman ADU Design & Construction in Milpitas
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Create a timeless Craftsman-style ADU in Milpitas with authentic architectural details and modern functionality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={openCalendlyModal}
                size="lg"
                className="bg-white text-amber-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Get Free Consultation
              </Button>
              <Link to="/cost-estimate">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-amber-900 px-8 py-4 text-lg font-semibold"
                >
                  Get Cost Estimate
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Craftsman Style */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Why Choose Craftsman ADU Design in Milpitas?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Handcrafted Quality</h3>
                <p className="text-gray-200">
                  Craftsman design emphasizes quality materials, detailed woodwork, and skilled craftsmanship that stands the test of time.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Natural Integration</h3>
                <p className="text-gray-200">
                  Designed to harmonize with nature, Craftsman ADUs blend seamlessly with Milpitas' natural landscape.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Built-in Character</h3>
                <p className="text-gray-200">
                  Rich architectural details like exposed beams, built-ins, and custom millwork create unique, characterful spaces.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Craftsman ADU Design Features
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Exposed Beams</h3>
                <p className="text-gray-200">
                  Authentic exposed structural beams showcase the craftsmanship and add architectural interest to interiors.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Custom Built-ins</h3>
                <p className="text-gray-200">
                  Handcrafted built-in cabinetry, bookshelves, and storage solutions maximize space efficiency.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Natural Materials</h3>
                <p className="text-gray-200">
                  Stone, wood, and earth-tone materials create the warm, organic feel characteristic of Craftsman design.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Wide Front Porches</h3>
                <p className="text-gray-200">
                  Covered porches with tapered columns create welcoming outdoor living spaces perfect for California weather.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Multi-pane Windows</h3>
                <p className="text-gray-200">
                  Traditional divided-light windows with wood frames allow abundant natural light while maintaining period authenticity.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Decorative Brackets</h3>
                <p className="text-gray-200">
                  Hand-carved brackets and trim work showcase the attention to detail that defines Craftsman architecture.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Milpitas Local Considerations */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Milpitas Craftsman ADU Considerations
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-white mb-4">Local Regulations</h3>
                <ul className="space-y-3 text-gray-200">
                  <li>• Maximum 1,200 sq ft for detached ADUs</li>
                  <li>• 5-foot minimum setbacks required</li>
                  <li>• Height limit of 16 feet maximum</li>
                  <li>• Owner-occupancy requirements apply</li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-white mb-4">Design Integration</h3>
                <ul className="space-y-3 text-gray-200">
                  <li>• Match existing home's architectural style</li>
                  <li>• Consider neighborhood character preservation</li>
                  <li>• Seismic safety requirements for bay area</li>
                  <li>• Energy efficiency compliance standards</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Craftsman ADU Investment in Milpitas
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Craftsman ADUs range from $220,000 to $450,000 depending on size and custom details. 
              The investment in quality craftsmanship provides exceptional long-term value and rental appeal.
            </p>
            <Link to="/cost-estimate">
              <Button
                size="lg"
                className="bg-white text-amber-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Get Detailed Cost Estimate
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Ready to Build Your Craftsman ADU in Milpitas?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Let's create a beautiful Craftsman ADU that honors traditional craftsmanship while meeting modern needs.
            </p>
            <Button
              onClick={openCalendlyModal}
              size="lg"
              className="bg-white text-amber-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
            >
              Schedule Your Free Consultation
            </Button>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              Explore More ADU Designs in Milpitas
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/contemporary-adu-design-milpitas" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Contemporary ADU Design Milpitas</h3>
                <p className="text-gray-200">Modern, sleek designs for today's lifestyle</p>
              </Link>
              <Link to="/mediterranean-adu-design-milpitas" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Mediterranean ADU Design Milpitas</h3>
                <p className="text-gray-200">Warm, elegant Mediterranean architecture</p>
              </Link>
              <Link to="/victorian-adu-design-milpitas" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Victorian ADU Design Milpitas</h3>
                <p className="text-gray-200">Classic Victorian charm and elegance</p>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </>
  );
};

export default CraftsmanADUDesignMilpitas;